// GamePopup Service để quản lý queue popup trong game
export interface GamePopupData {
  id: string;
  type: 'welcome' | 'checkin' | 'energy' | 'info' | 'warning' | 'success' | 'error' | 'top10';
  title?: string;
  message: string;
  icon?: string;
  actionText?: string;
  onAction?: () => void;
  duration?: number; // Thời gian hiển thị (ms), mặc định 4000ms
  dismissible?: boolean; // Có thể đóng bằng tay hay không, mặc định true
}

class GamePopupService {
  private popupQueue: GamePopupData[] = [];
  private isProcessing = false;
  private currentPopupData: GamePopupData | null = null;
  private listeners: (() => void)[] = [];
  private readonly DELAY_BETWEEN_POPUPS = 500; // ms
  private currentPopupTimeout: NodeJS.Timeout | null = null;

  // Đăng ký listener để component có thể lắng nghe thay đổi
  subscribe(listener: () => void) {
    this.listeners.push(listener);
    // Initial call to sync state
    listener();
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Thông báo cho tất cả listeners
  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // Thêm popup vào queue
  showPopup(popup: Omit<GamePopupData, 'id'>): string {
    const id = `popup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const popupData: GamePopupData = {
      id,
      dismissible: true,
      ...popup,
    };

    this.popupQueue.push(popupData);
    if (!this.isProcessing) {
      this.processQueue();
    }
    return id;
  }
  
  // Hiển thị nhiều popup với delay
  showPopups(popups: Omit<GamePopupData, 'id'>[]): string[] {
    const ids = popups.map(popup => this.showPopup(popup));
    return ids;
  }

  // Xử lý queue popup
  private processQueue() {
    if (this.popupQueue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const popup = this.popupQueue.shift();

    if (!popup) {
      this.isProcessing = false;
      return;
    }

    this.currentPopupData = popup;
    this.notifyListeners();

    if (popup.duration) {
      this.currentPopupTimeout = setTimeout(() => {
        // Use a flag to indicate this is an automatic dismissal from the timer
        this.dismissCurrentPopup(true); 
      }, popup.duration);
    }
  }

  // Đóng popup hiện tại (nếu dismissible)
  dismissCurrentPopup(isAutoDismiss = false) {
    if (!this.currentPopupData) return;

    // If it's a manual dismiss (isAutoDismiss is false) and the popup is not dismissible, do nothing.
    if (!isAutoDismiss && this.currentPopupData.dismissible === false) {
      return;
    }

    // Clear any existing timer to prevent it from firing after dismissal
    if (this.currentPopupTimeout) {
      clearTimeout(this.currentPopupTimeout);
      this.currentPopupTimeout = null;
    }

    this.currentPopupData = null;
    this.notifyListeners();

    // After dismissing, wait a moment before processing the next item in the queue.
    setTimeout(() => {
      this.processQueue();
    }, this.DELAY_BETWEEN_POPUPS);
  }

  // Lấy popup hiện tại
  getCurrentPopup(): GamePopupData | null {
    return this.currentPopupData;
  }

  // Xóa tất cả popup
  clearAll() {
    this.popupQueue = [];
    if (this.currentPopupTimeout) {
      clearTimeout(this.currentPopupTimeout);
      this.currentPopupTimeout = null;
    }
    this.currentPopupData = null;
    this.isProcessing = false;
    this.notifyListeners();
  }
}

// Export singleton instance
export const gamePopupService = new GamePopupService();
export default gamePopupService;
